/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { MutableRefObject } from "react";
import { DropDownResponse, OptionTypeBase } from "./common";
import { GroupedSite } from "./site";

type OnDrop = (
  targetParentId: string,
  draggedItem: string,
  targetIndex: number
) => void;

export type ConditionError = {
  id: string;
  type: string;
  // set for errors that don't come from the local validator (e.g. returned by the backend)
  message?: string;
  // input the message belongs to, e.g. "tag" or "value"
  field?: string;
};

export type ConditionData = {
  property?: string;
  operator?: string;
  value?: string;
  from?: string;
  to?: string;
  type?: string;
  tag?: string;
  timeZone?: string;
  hl7Field?: string;
  script?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations?: any[];
  modality?: string;
  gender?: string;
  bodyPart?: string;
  examCode?: string;
  organisationId?: string;
  siteIds?: string[];
  caseSensitive?: boolean; // default is true
};

export type CollectionConditionData = {
  operator?: string;
  values?: string[];
  property?: string;
};

export type IntegerRangeConditionData = {
  operator?: string;
  from?: string;
  to?: string;
  value?: string;
  property?: string;
};

/** New types */
export type OrganisationConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'organisationId'
};

export type SiteConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'siteId'
};

export type ModalityConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'modality'
};

export type PatientClassRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'patientClass'
};

export type ReportingPriorityConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'reportingPriority'
};

export type GenderConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'gender'
};

export type BodyPartConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'bodyPart'
};

export type ExamCodeConditionRule = {
  id: string;
  scope?: "conditions";
  type: string;
  stringCollectionProperty?: CollectionConditionData; // property = 'examCode'
};
export type AgeRangeConditionRule = {
  id: string;
  scope: "conditions";
  type: string;
  integerRangeProperty?: IntegerRangeConditionData;
};

export type StudyDescriptionConditionRule = {
  id: string;
  scope: "conditions";
  type: string;
  stringProperty?: ConditionData;
};

export type DynamicRuleConditionData = {
  id: string;
  scope?: "conditions";
  type: string;
  dynamic?: ConditionData;
};

export type DicomRuleConditionData = {
  id: string;
  scope?: "conditions";
  type: string;
  dicom?: ConditionData;
};

export type DayOfWeekRuleConditionData = {
  id: string;
  scope: "conditions";
  type: string;
  dayOfWeek?: ConditionData;
};

export type TimeOfDayRuleConditionData = {
  id: string;
  scope: "conditions";
  type: string;
  timeOfDay?: ConditionData;
};

export type ScriptRuleConditionData = {
  id: string;
  scope: "conditions";
  type: string;
  script?: ConditionData;
};

export type HL7ServiceRuleConditionData = {
  id: string;
  scope?: "conditions";
  type: string;
  hl7?: HL7ServiceData;
};

export type ConditionObjectData = {
  id: string;
  scope: "conditions";
  type: string;
  hl7?: HL7ServiceData;
  dicom?: ConditionData;
  timeOfDay?: ConditionData;
  stringProperty?: ConditionData;
  dayOfWeek?: ConditionData;
  script?: ConditionData;
  dynamic?: ConditionData;
  stringCollectionProperty?: CollectionConditionData; // property can be 'organisationId' or 'siteId' or 'modality', ...
  integerRangeProperty?: IntegerRangeConditionData; // property can be 'ageRange', ...
  showOperator?: boolean;
};

export type TransformObjectData = {
  id: string;
  type: string;
  transform?: TransformData;
  dicom?: ConditionData;
  timeOfDay?: ConditionData;
  ageRange?: ConditionData;
  dayOfWeek?: ConditionData;
  script?: ConditionData;
  dynamic?: ConditionData;
};

export type ConditionRuleData = {
  id: string;
  scope: "rules";
  operator: "AND" | "OR";
  conditions: ConditionObjectData[];
  conditionRules: ConditionRuleData[];
};

export type UntaggedConditionObjectData = {
  id?: string;
  scope?: "conditions";
  type?: string;
  hl7?: HL7ServiceData;
  dicom?: ConditionData;
  timeOfDay?: ConditionData;
  dayOfWeek?: ConditionData;
  script?: ConditionData;
  dynamic?: ConditionData;
  modality?: string;
  gender?: string;
  organisationId?: string;
  stringCollectionProperty?: CollectionConditionData; // property can be 'organisationId' or 'siteId' or 'modality' or 'gender', ...
  integerRangeProperty?: IntegerRangeConditionData; // property can be 'ageRange', ...
  stringProperty?: ConditionData; // property can be 'studyDescription', ...
};

export type Anonymize = {
  retainDeviceIdentityOption: boolean;
  retainLongitudinalTemporalInformationFullDatesOption: boolean;
  retainInstitutionIdentityOption: boolean;
  retainUidsOption: boolean;
  retainDescriptionsOption: boolean;
  retainBurnedInAnnotationsOption: boolean;
};

export const defaultAnonymize: Readonly<Anonymize> = {
  retainDeviceIdentityOption: false,
  retainLongitudinalTemporalInformationFullDatesOption: false,
  retainInstitutionIdentityOption: false,
  retainUidsOption: false,
  retainDescriptionsOption: false,
  retainBurnedInAnnotationsOption: false,
};

export type TransformData = {
  value?: string;
  source?: string;
  destination?: string;
  tag?: string;
  operator?: string;
  from?: string;
  to?: string;
  script?: string;
  regex?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations?: any[];
  anonymize?: Anonymize;
  type?: string;
  timeZone?: string;
};

export type TransformResponseData = {
  value?: string;
  path?: string;
  tag?: string;
  regex?: string;
  source?: string;
  operator?: string;
  script?: string;
  sourcePath?: string;
  destinationPath?: string;
  destination?: string;
  from?: string;
  to?: string;
  anonymize?: Anonymize;
  type?: string;
  timeZone?: string;
  relativeTo?: string;
};

export type NumberOfOrdersData = {
  groupedOrderCount?: number;
  operator?: string;
};

export type HL7ServiceData = {
  operator?: string;
  value?: string;
  from?: string;
  to?: string;
  path?: string;
  tag?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations?: any[];
};

export type UntaggedTransformObjectResponse = {
  index?: number;
  anonymize?: Anonymize;
  copy?: TransformResponseData;
  regex?: TransformResponseData;
  constant?: TransformResponseData;
  remove?: TransformResponseData;
  script?: TransformResponseData;
  dicom?: TransformResponseData;
  timeOfDay?: TransformResponseData;
  dayOfWeek?: TransformResponseData;
  dynamic?: TransformResponseData;
};

export type UntaggedTransformObjectData = {
  id?: string;
  type?: string;
  transform: TransformData;
  dicom?: TransformData;
  dynamic?: TransformData;
};

export type UntaggedConditionRuleData = {
  id?: string;
  scope?: "rules";
  operator: "AND" | "OR";
  conditions: UntaggedConditionObjectData[];
  conditionRules: UntaggedConditionRuleData[];
};

type OnValueChangeHandler = (
  value:
    | DicomRuleConditionData
    | DayOfWeekRuleConditionData
    | TimeOfDayRuleConditionData
    | ScriptRuleConditionData
    | HL7ServiceRuleConditionData
    | DynamicRuleConditionData
    | OrganisationConditionRule
    | SiteConditionRule
    | ReportingPriorityConditionRule
    | PatientClassRule
    | GenderConditionRule
    | BodyPartConditionRule
    | ExamCodeConditionRule
    | AgeRangeConditionRule
    | StudyDescriptionConditionRule
    | ModalityConditionRule,
  parent: string
) => void;

export type ConditionFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  condition?: ConditionData;
  disabled?: boolean;
  isHasScope?: boolean;
  isFromTransform?: boolean;
  isConditionRule?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

export type StudyDescriptionConditionFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  condition?: ConditionData;
  disabled?: boolean;
  isHasScope?: boolean;
  isFromTransform?: boolean;
  isConditionRule?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

export type AgeRangeFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  condition?: IntegerRangeConditionData;
  disabled?: boolean;
  isHasScope?: boolean;
  isDark?: boolean;
};

export type ModalityFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  modality?: string[];
  operator?: string;
  showOperator?: boolean;
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type PatientClassFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  patientClass?: string[];
  operator?: string;
  showOperator?: boolean;
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type GenderFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  gender?: string[];
  operator?: string;
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type BodyPartFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  bodyPart?: string[];
  operator?: string;
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type StudyDescriptionFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  studyDescription?: string[];
  operator?: string;
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type ExamCodeFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  codes?: string[];
  operator?: string;
  disabled?: boolean;
  isDark?: boolean;
  options?: DropDownResponse<string>[];
  allCodes?: OptionTypeBase[];
};

export type SiteFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  siteIds?: string[];
  disabled?: boolean;
  isDark?: boolean;
  options?: GroupedSite[];
};

export type OrganisationFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  organisationIds?: string[];
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export type HL7ServiceFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  hl7?: HL7ServiceData;
  disabled?: boolean;
  isDark?: boolean;
};

export enum TransformTypeEnum {
  DICOM,
  HL7,
  PREFETCHING_RULE,
  DYNAMIC,
}

export type TransformFieldProps = {
  onValueChangeHandler: OnTransformValueChangeHandler;
  id: string;
  errors: ConditionError[];
  transform?: TransformData;
  disabled?: boolean;
  transformType: TransformTypeEnum;
  fullWidth?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

type OnRemoveCondition = (id: string, parentId: string) => void;
type Validate = () => void;
export type Validator = {
  validate: Validate;
};

export type RuleBuilderProps = {
  ruleTypes: string[];
  disabled?: boolean;
  initialValue?: UntaggedConditionRuleData | null;
  ref: MutableRefObject<Validator>;
  isDark?: boolean;
  organisationOptions?: OptionTypeBase[];
  siteOptions?: GroupedSite[];
  modalityOptions?: OptionTypeBase[];
  patientClassOptions?: OptionTypeBase[];
  showModalityOperator?: boolean;
  reportPriorityOptions?: OptionTypeBase[];
  genderOptions?: OptionTypeBase[];
  bodyPartOptions?: OptionTypeBase[];
  examCodeOptions?: DropDownResponse<string>[];
  onHandleScriptModal?: (open?: boolean) => void;
};

type OnDropOut = (id: string) => void;
export type TransformBuilderProps = {
  ruleTypes: string[];
  disabled?: boolean;
  initialValue?: UntaggedTransformObjectData[];
  ref: MutableRefObject<Validator>;
  title?: string;
  noResult?: string;
  addBtnText?: string;
  onDropOut?: OnDropOut;
  transformType: TransformTypeEnum;
  hideCollapse?: boolean;
  titleConfirmRemove?: string;
  isDynamicConditionType?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

type OnAddCondition = (condition: ConditionData, parentId: string) => void;
type OnAddConditionRule = (
  conditionRule: ConditionRuleData,
  parentId: string
) => void;
type CreateCondition = (id: string) => void;
type CreateConditionRule = (id: string) => void;
type OnRemoveConditionRule = (id: string, parentId: string) => void;
type OnBooleanSelect = (id: string, value: string) => void;

export type ConditionProps = {
  id: string;
  onValueChangeHandler: OnValueChangeHandler;
  disabled?: boolean;
  parentId: string;
  errors: ConditionError[];
  ruleTypes: string[];
  onRemoveCondition: OnRemoveCondition;
  type?: string;
  condition: ConditionObjectData;
  isDark?: boolean;
  organisationOptions?: OptionTypeBase[];
  siteOptions?: GroupedSite[];
  modalityOptions?: OptionTypeBase[];
  patientClassOptions?: OptionTypeBase[];
  showModalityOperator?: boolean;
  reportPriorityOptions?: OptionTypeBase[];
  genderOptions?: OptionTypeBase[];
  bodyPartOptions?: OptionTypeBase[];
  examCodeOptions?: DropDownResponse<string>[];
  onHandleScriptModal?: (open?: boolean) => void;
};

export type ConditionRuleProps = {
  id: string;
  onAddCondition?: OnAddCondition;
  createCondition?: CreateCondition;
  createConditionRule?: CreateConditionRule;
  onAddConditionRule?: OnAddConditionRule;
  onRemoveConditionRule?: OnRemoveConditionRule;
  onBooleanSelect: OnBooleanSelect;
  onValueChangeHandler: OnValueChangeHandler;
  disabled?: boolean;
  parentId: string | null;
  value: ConditionRuleData;
  ruleTypes: string[];
  onRemoveCondition: OnRemoveCondition;
  type?: string;
  errors: ConditionError[];
  onDropCondition: OnDrop;
  onDropConditionRule: OnDrop;
  isDark?: boolean;
  organisationOptions?: OptionTypeBase[];
  siteOptions?: GroupedSite[];
  modalityOptions?: OptionTypeBase[];
  patientClassOptions?: OptionTypeBase[];
  showModalityOperator?: boolean;
  reportPriorityOptions?: OptionTypeBase[];
  genderOptions?: OptionTypeBase[];
  bodyPartOptions?: OptionTypeBase[];
  examCodeOptions?: DropDownResponse<string>[];
  onHandleScriptModal?: (open?: boolean) => void;
};

type OnAddTransform = (condition: ConditionData) => void;
type OnRemoveTransform = (id: string) => void;
type CreateTransform = () => void;
type OnTransformValueChangeHandler = (value: TransformObjectData) => void;
type OnDropTransform = (draggedItem: string, targetIndex: number) => void;

export type TransformerProps = {
  onAddTransform?: OnAddTransform;
  createTransform: CreateTransform;
  onValueChangeHandler: OnTransformValueChangeHandler;
  disabled?: boolean;
  value: TransformObjectData[];
  ruleTypes: string[];
  onRemoveTransform: OnRemoveTransform;
  type?: string;
  noResult?: string;
  addBtnText?: string;
  errors: ConditionError[];
  onDropTransform: OnDropTransform;
  title?: string;
  transformType: TransformTypeEnum;
  hideCollapse?: boolean;
  titleConfirmRemove?: string;
  isDynamicConditionType?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

export type DroppedConditionData = {
  value: ConditionObjectData | ConditionRuleData;
  parentId: string;
};

export type TransformProps = {
  id: string;
  onValueChangeHandler: OnTransformValueChangeHandler;
  disabled?: boolean;
  errors: ConditionError[];
  ruleTypes: string[];
  usedTypes?: string[];
  onRemoveTransform: OnRemoveTransform;
  type?: string;
  transform: TransformObjectData;
  transformType: TransformTypeEnum;
  titleConfirmRemove?: string;
  isDynamicConditionType?: boolean;
  isDark?: boolean;
  onHandleScriptModal?: (open?: boolean) => void;
};

export type ReportingPriorityFieldProps = {
  onValueChangeHandler: OnValueChangeHandler;
  id: string;
  errors: ConditionError[];
  parent: string;
  priorities?: string[];
  disabled?: boolean;
  isDark?: boolean;
  options?: OptionTypeBase[];
};

export const PREFETCHING_RULE_CONDITIONS = [
  "DICOM",
  "DAY_OF_WEEK",
  "TIME_OF_DAY",
  "DYNAMIC",
];

export const PREFETCHING_RULE_QUERY = ["CONSTANT", "COPY", "REGEX", "DYNAMIC"];

export const PREFETCHING_RULE_FILTER = [
  "DICOM",
  "SCRIPT",
  "DYNAMIC",
  "AI_RELEVANCY",
];
